import React, { useEffect, useRef, useState } from 'react';
import { generateDefaultVisageTemplates } from './defaultTemplates/DefaultTemplates';
import { synchronizeTemplateToSteps } from './GrapesJSUtilities';
import Dialog from '../layout/Dialog';
import Panel from '../layout/Panel';
import { IVisageTemplate } from '../../interfaces/VisageUI';
import BoolProperty from '../properties/BoolProperty';
import Spacer from '../layout/Spacer';
import { useTypedDispatch, useTypedSelector } from '../../state/hooks';
import { StoreActions } from '../../state/store';
import { assert } from '../../classes/utilities/ErrorHandling';
import Button from '../layout/Button';
import { deepEqual, JsonCopy } from '../../classes/utilities/Misc';

export default function ResetFactoryTemplatesDialog(props)
{
	const dispatch = useTypedDispatch();

	const projectTemplates = useTypedSelector<{ [templateId: string]: IVisageTemplate }>(state => state.visageModule.uiTemplateData);

	const [factoryTemplates, setFactoryTemplates] = useState<{ [templateId: string]: IVisageTemplate }>({});
	const [templatesToReset, setTemplatesToReset] = useState<string[]>([]);

	const templateUsageMap = useRef(new Map<string, IVisageTemplate[]>());// factoryTemplateId -> projectTemplateId[]

	useEffect(() => 
	{
		const defaultTemplates = generateDefaultVisageTemplates();
		setFactoryTemplates(defaultTemplates);

		// update usage map
		templateUsageMap.current = new Map<string, IVisageTemplate[]>();	
		const defaultTemplateNames = new Set<string>();
		for(const [templateId, templateData] of Object.entries<IVisageTemplate>(defaultTemplates))
		{
			defaultTemplateNames.add(templateData.name);
			templateUsageMap.current.set(templateData.name, []);
		}
		
		for(const projectTemplateData of Object.values<IVisageTemplate>(projectTemplates))
		{
			if(!projectTemplateData.defaultTemplateID) continue;

			if(!templateUsageMap.current.has(projectTemplateData.defaultTemplateID))
			{
				console.warn(`Project template ${projectTemplateData.name} uses a default template that does not exist: ${projectTemplateData.defaultTemplateID}`);
				continue;
			}

			assert(templateUsageMap.current.has(projectTemplateData.defaultTemplateID), `Project template ${projectTemplateData.name} uses a default template that does not exist: ${projectTemplateData.defaultTemplateID}`);
			templateUsageMap.current.get(projectTemplateData.defaultTemplateID)!.push(projectTemplateData);
		}
			
		// automatically mark missing templates for restoration
		// const missingDefaultTemplates = new Array<string>();
		// for(const [templateId, templateData] of Object.entries<IVisageTemplate>(defaultTemplates))
		// {
		// 	if(templateUsageMap.current.get(templateData.name)?.length) continue;
		// 	missingDefaultTemplates.push(templateId);
		// }
		// setTemplatesToReset(missingDefaultTemplates);
	}, []);

	function renderTemplatesList()
	{
		const output = new Array<JSX.Element>();
		const factoryTemplatesEntries = Object.entries<IVisageTemplate>(factoryTemplates).sort((a, b) => a[1].name.localeCompare(b[1].name));
		for(const [factoryTemplateId, factoryTemplateData] of factoryTemplatesEntries)
		{
			const templateUsage = templateUsageMap.current.get(factoryTemplateData.name) ?? [];
			const wasTemplateModified = templateUsage.some(projectTemplateData => !deepEqual(projectTemplateData.content, factoryTemplateData.content));
			const displayLabel = 
			<>
				{factoryTemplateData.name}
				<Spacer size={{ x: "4px" }} />
				<span className={"dimmed"}>
					{
						!templateUsage?.length ?
							`- Deleted`
							:
							templateUsage.length === 1 && templateUsage.map(templateData => templateData.name)[0] === factoryTemplateData.name ?
								null
								:
								`- Current Name: ${templateUsage.map(templateData => templateData.name).join(", ")}`
					}
					{
						wasTemplateModified ?
							" - Modified"
							:
							null
					}
				</span>
			</>;

			// only show deleted or modified templates in the list
			if(wasTemplateModified || !templateUsage?.length)
			{
				output.push(
					<BoolProperty data-name={`select-template-${factoryTemplateId}-checkbox`}
						key={factoryTemplateId}
						labelAfterProperty={displayLabel}
						value={templatesToReset.includes(factoryTemplateId)}
						onChangeCallback={(newValue) =>
						{
							if(newValue)
							{
								setTemplatesToReset([...templatesToReset, factoryTemplateId]);
							}
							else
							{
								setTemplatesToReset(templatesToReset.filter(id => id !== factoryTemplateId));
							}
						}}
					/>
				);
			}
		}

		console.log("No templates to reset", output.length);
		if(!output.length)
		{
			output.push(<p>All templates are already in their default state.</p>)
		}

		return output;
	}

	return (
		<Dialog title={"Reset Templates to Default"}
			data-name={"reset-factory-templates-dialog"}
			showCloseButton
			onShutdown={props.onShutdown}
			actions={[
				{
					label: "Confirm",
					onClick: ()=>
					{
						// reset here
						for(const templateId of templatesToReset)
						{
							const defaultTemplate = factoryTemplates[templateId];
							assert(defaultTemplate, `Factory template ${templateId} does not exist`);

							const templateUsage = templateUsageMap.current.get(defaultTemplate.name);
							console.log(templateId, templateUsage, templateUsageMap.current)
							
							// check usage map - if no templates use this one, create it
							if(!templateUsage || !templateUsage.length)
							{
								dispatch(StoreActions.visage.setUITemplateData({ templateId: templateId, data: JsonCopy(defaultTemplate) }));
							}
							else
							{
								// overwrite data of templates that use the default one
								for(const projectTemplateData of templateUsage)
								{
									const newTemplateData: IVisageTemplate = { ...projectTemplateData,
										content: JsonCopy(defaultTemplate.content),
										previousState: JsonCopy(defaultTemplate.content),
										name: defaultTemplate.name,
									};
									dispatch(StoreActions.visage.setUITemplateData({ templateId: projectTemplateData.id, data: newTemplateData }));
									synchronizeTemplateToSteps(projectTemplateData.id);
								}
							}
						}
					},
				},
				{
					label: "Cancel",
				}
			]}
			style={{ maxWidth: "600px", height: "500px" }}
		>
			Select templates to reset:
			<Spacer size={{ y: "10px" }} />
			<Button data-name={"select-all-templates-button"}
				label={templatesToReset.length === Object.keys(factoryTemplates).length ? "Deselect All" : "Select All"}
				onClick={() => 
				{
					if(templatesToReset.length === Object.keys(factoryTemplates).length)
					{
						setTemplatesToReset([]);
					}
					else
					{
						setTemplatesToReset(Object.keys(factoryTemplates));
					}
				}}
			/>
			<Spacer size={{ y: "10px" }} />
			<Panel data-name={"template-select-container"}
				className={"solid padded extra"}
				style={{ maxHeight: "400px", overflowX: "hidden" }}
			>
				{ renderTemplatesList() }
			</Panel>
			<Spacer size={{ y: "10px" }} />
			<p className={"warningText"}>
				This will restore the templates their default settings. This action cannot be undone.
			</p>
		</Dialog>
	);
}